import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocalState } from "../../utils/hooks/global/useLocalStorage";
import CustomButton from "../global/CustomButton";
import { Loading } from "../global/Loading";

const RECORDS_SHOWN = 8;

const sameValue = (a, b) =>
  String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

const formatBytes = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const codeSx = {
  fontFamily: "monospace",
  fontSize: 12,
  overflowWrap: "anywhere",
};

const Preview = ({ text }) =>
  text ? (
    <Box
      component="pre"
      sx={{
        ...codeSx,
        m: 0,
        mt: 1,
        p: 1.5,
        maxHeight: 180,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        bgcolor: "#f4f6f8",
        borderRadius: "8px",
      }}
    >
      {text}
    </Box>
  ) : null;

// One field of an ADA record, with buttons to use it for the check
const FieldRow = ({ field, deposit, withdrawal, upValues, onUse, onUseUpValue }) => {
  const usedFor = [
    field.expression === deposit && "deposit",
    field.expression === withdrawal && "withdrawal",
  ].filter(Boolean);
  const isUpValue = upValues.some((up) => sameValue(up, field.value));

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 1.5,
        rowGap: 0.5,
        py: 0.75,
        borderTop: "1px solid #eef0f2",
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: field.likelyStatus ? 700 : 400,
          minWidth: 140,
        }}
      >
        {field.key}
      </Typography>
      <Typography sx={{ ...codeSx, flex: "1 1 120px", minWidth: 0 }}>
        {JSON.stringify(field.value)}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {["deposit", "withdrawal"].map((type) => (
          <Button
            key={type}
            size="small"
            variant={usedFor.includes(type) ? "contained" : "outlined"}
            color="inherit"
            disableElevation
            onClick={() => onUse(type, field.expression)}
            sx={{
              textTransform: "none",
              fontSize: 12,
              py: 0.1,
              minWidth: 0,
              borderColor: "rgba(33, 43, 54, 0.3)",
              ...(usedFor.includes(type) && {
                bgcolor: "rgb(33, 43, 54)",
                color: "white",
                "&:hover": { bgcolor: "rgba(33, 43, 54, 0.85)" },
              }),
            }}
          >
            {usedFor.includes(type) ? `✓ ${type}` : `Use for ${type}`}
          </Button>
        ))}
        {usedFor.length > 0 && !isUpValue && (
          <Button
            size="small"
            onClick={() => onUseUpValue(field.value)}
            sx={{ textTransform: "none", fontSize: 12, py: 0.1 }}
          >
            Count {JSON.stringify(field.value)} as up
          </Button>
        )}
      </Box>
    </Box>
  );
};

// Fetches the check's API URL and lists the records about ADA in the
// response. Clicking a field fills in the lookup expression, so a broken or
// new API check can be set up without writing the expression by hand.
const ApiResponseExplorer = ({
  autoFetch = false,
  url,
  deposit,
  withdrawal,
  upValues,
  onUse,
  onUseUpValue,
}) => {
  const [jwt] = useLocalState(null, "jwt");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState();
  const [fetchedUrl, setFetchedUrl] = useState();
  const [filter, setFilter] = useState("");
  const [showAll, setShowAll] = useState(false);

  const explore = async () => {
    setLoading(true);
    setFilter("");
    setShowAll(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/exchanges/exploreSource/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + jwt,
          },
          body: JSON.stringify({ url }),
        }
      );
      const data = await response.json().catch(() => ({}));
      setResult(
        response.ok
          ? data
          : { errorMessage: data.message || `Request failed (${response.status})` }
      );
    } catch (e) {
      setResult({ errorMessage: e.message });
    } finally {
      setFetchedUrl(url);
      setLoading(false);
    }
  };

  // Fetch right away when the editor just switched to this URL
  useEffect(() => {
    if (autoFetch && url) explore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const records = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!result?.records || !query) return result?.records ?? [];
    return result.records
      .map((record) => ({
        ...record,
        fields: record.fields.filter((field) =>
          `${field.key} ${JSON.stringify(field.value)}`
            .toLowerCase()
            .includes(query)
        ),
      }))
      .filter(
        (record) =>
          record.fields.length ||
          record.expression.toLowerCase().includes(query)
      );
  }, [result, filter]);

  const shown = showAll ? records : records.slice(0, RECORDS_SHOWN);

  return (
    <Box
      sx={{
        border: "1px solid #dfe3e8",
        borderRadius: "12px",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            Find the status in the API response
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fetches the URL and lists everything about ADA. Pick the fields
            for deposit and withdrawal.
          </Typography>
        </Box>
        <CustomButton
          buttonText={result ? "Fetch again" : "Fetch response"}
          color="blackOutlined"
          disabled={!url || loading}
          onClick={explore}
        />
      </Box>

      {loading && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Loading />
        </Box>
      )}

      {!loading && result && (
        <Box sx={{ mt: 2 }}>
          {fetchedUrl !== url && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              The URL changed since this was fetched. Fetch again to see the
              new response.
            </Alert>
          )}
          {result.errorMessage ? (
            <Alert severity="error">
              {result.errorMessage}
              <Preview text={result.preview} />
            </Alert>
          ) : !result.records?.length ? (
            <Alert severity="warning">
              ADA is not in this response (HTTP {result.httpStatus},{" "}
              {formatBytes(result.sizeBytes)}). The exchange may have delisted
              ADA or moved it to another endpoint.
              <Preview text={result.preview} />
            </Alert>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {result.records.length} record
                  {result.records.length > 1 ? "s" : ""} about ADA · HTTP{" "}
                  {result.httpStatus} · {formatBytes(result.sizeBytes)} · up
                  value{upValues.length > 1 ? "s" : ""}:{" "}
                  <Box component="span" sx={codeSx}>
                    {upValues.map((up) => JSON.stringify(up)).join(", ")}
                  </Box>
                </Typography>
                {result.records.length > 2 && (
                  <TextField
                    size="small"
                    placeholder="Filter fields or values"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: { xs: "100%", sm: 240 } }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {shown.map((record) => (
                  <Box
                    key={record.expression}
                    sx={{
                      border: "1px solid #eef0f2",
                      borderRadius: "8px",
                      px: 1.5,
                      pt: 1,
                      pb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Chip
                        size="small"
                        label={record.matchedBy}
                        sx={{ height: 22, fontSize: 12 }}
                      />
                      {record.usesPosition && (
                        <Tooltip title="The lookup uses a position in a list, so it breaks if the exchange reorders the list">
                          <Chip
                            size="small"
                            label="uses list position"
                            sx={{
                              height: 22,
                              fontSize: 12,
                              backgroundColor: "rgba(255, 171, 0, 0.16)",
                              color: "rgb(183, 110, 0)",
                            }}
                          />
                        </Tooltip>
                      )}
                      <Typography
                        color="text.secondary"
                        sx={{ ...codeSx, flex: "1 1 200px", minWidth: 0 }}
                      >
                        {record.expression || "(top level)"}
                      </Typography>
                    </Box>
                    {record.fields.map((field) => (
                      <FieldRow
                        key={field.key}
                        field={field}
                        deposit={deposit}
                        withdrawal={withdrawal}
                        upValues={upValues}
                        onUse={onUse}
                        onUseUpValue={onUseUpValue}
                      />
                    ))}
                  </Box>
                ))}
                {!records.length && (
                  <Typography variant="body2" color="text.secondary">
                    Nothing matches “{filter}”.
                  </Typography>
                )}
              </Box>
              {records.length > RECORDS_SHOWN && (
                <Button
                  size="small"
                  onClick={() => setShowAll(!showAll)}
                  sx={{ mt: 1, textTransform: "none" }}
                >
                  {showAll ? "Show fewer" : `Show all ${records.length}`}
                </Button>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ApiResponseExplorer;
